import React from "react";
import { Box, Typography, Card, CardContent } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import { useNavigate, useLocation } from "react-router-dom";
import { getPageContext, refetchBus, buildRefetchKey } from "@ippon/shared-utils";
import { colors } from "../theme/colors";
import type { ReferencedDocument } from "./AIChatTypes";
import CustomTruncateText from "../TruncateText/CustomTruncateText";
import { CustomFileModal } from "@ippon/shared-components";

interface ReferencedDocumentsProps {
  documents: ReferencedDocument[];
  primaryColor?: string;
  showLabels?: boolean;
}

interface GroupedDocument {
  document_name: string;
  file_url?: string;
  document_type: string;
  property_name?: string;
  property_id?: string;
  source_type?: ReferencedDocument["source_type"];
  entity_label?: string;
  unit_id?: string;
  contract_id?: string;
  repair_id?: string;
  expense_id?: string;
  pages: {
    document_id: string;
    page_number?: number;
    similarity_score?: number;
    dates?: ReferencedDocument["dates"];
  }[];
}

interface DocumentNameWithPagesProps {
  fileName: string;
}

const DocumentNameWithPages: React.FC<DocumentNameWithPagesProps> = ({
  fileName,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        mb: 0.5,
        minWidth: 0,
        flexWrap: "nowrap",
      }}
    >
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 500,
          color: colors.text.primary,
          fontFamily: "'Noto Sans JP', sans-serif",
          minWidth: 0,
          width: "100%",
        }}
      >
        <CustomTruncateText text={fileName} />
      </Typography>
    </Box>
  );
};

export const ReferencedDocuments: React.FC<ReferencedDocumentsProps> = ({
  documents,
  primaryColor = colors.primary.main,
  showLabels = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  // 既に同じパスに居る場合は navigate しても再マウントされず再取得されないため、
  // 現在画面の entity/id (data-entity / data-id) を refetchBus に通知して
  // 購読側 (useRefetchOn) を再フェッチさせる。別パスは通常遷移に任せる。
  const navigateOrRefetch = (to: string) => {
    const targetPath = to.split(/[?#]/)[0];
    if (targetPath === location.pathname) {
      getPageContext().entities?.forEach((en) => {
        if (en.entity && en.id) {
          refetchBus.emit(buildRefetchKey(en.entity, en.id));
        }
      });
    }
    navigate(to);
  };
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewFile, setPreviewFile] = React.useState<{
    url: string;
    name: string;
  } | null>(null);

  const handleClosePreview = React.useCallback(() => {
    setPreviewOpen(false);
    setPreviewFile(null);
  }, []);

  if (!documents || documents.length === 0) return null;

  const groupDocuments = (docs: ReferencedDocument[]): GroupedDocument[] => {
    const groupedMap = new Map<string, GroupedDocument>();

    docs.forEach((doc) => {
      const key = `${doc.document_name}_${doc.file_url || ""}_${doc.property_id || ""}`;

      if (groupedMap.has(key)) {
        const grouped = groupedMap.get(key)!;
        grouped.pages.push({
          document_id: doc.document_id,
          page_number: doc.page_number,
          similarity_score: doc.similarity_score,
          dates: doc.dates,
        });
      } else {
        groupedMap.set(key, {
          document_name: doc.document_name,
          file_url: doc.file_url,
          document_type: doc.document_type,
          property_name: doc.property_name,
          property_id: doc.property_id,
          source_type: doc.source_type,
          entity_label: doc.entity_label,
          unit_id: doc.unit_id,
          contract_id: doc.contract_id,
          repair_id: doc.repair_id,
          expense_id: doc.expense_id,
          pages: [
            {
              document_id: doc.document_id,
              page_number: doc.page_number,
              similarity_score: doc.similarity_score,
              dates: doc.dates,
            },
          ],
        });
      }
    });

    return Array.from(groupedMap.values());
  };

  const groupedDocs = groupDocuments(documents);

  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      {showLabels && (
        <Typography
          sx={{
            fontSize: 12,
            color: "#989898",
            mb: 1,
            fontFamily: "'Noto Sans JP', sans-serif",
          }}
        >
          記載先リンク
        </Typography>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {groupedDocs.map((grouped, index) => {
          const fullFileName = `${grouped.property_name ? `${grouped.property_name} - ` : ""}${grouped.document_name}`;

          const navigable = !!(
            grouped.file_url ||
            grouped.repair_id ||
            grouped.expense_id ||
            grouped.property_id
          );

          const handleCardClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            // (1) 出所の種別に応じた画面へ遷移する（チャットは遷移しても開いたまま）。
            if (grouped.source_type === "repair_estimate" && grouped.repair_id) {
              navigateOrRefetch(`/repairs/${grouped.repair_id}`);
            } else if (
              grouped.source_type === "expense_doc" &&
              grouped.expense_id
            ) {
              navigateOrRefetch(`/expenses/${grouped.expense_id}`);
            } else if (grouped.property_id) {
              // 物件書類・契約書類(契約は物件配下のため)・その他は物件の書類タブへ。
              // chatDoc state は渡さない（受け側がアコーディオンを自動展開してしまうため）。
              // PDF は下の setPreviewFile でその場プレビューする。
              navigateOrRefetch(`/properties/${grouped.property_id}?subtab=2`);
            }
            // (2) 同時にPDFプレビューも開く（遷移先の画面の上にモーダル表示）。
            if (grouped.file_url) {
              setPreviewFile({
                url: grouped.file_url,
                name: fullFileName,
              });
              setPreviewOpen(true);
            }
          };

          return (
            <Card
              key={`${grouped.document_name}_${index}`}
              onClick={handleCardClick}
              sx={{
                backgroundColor: "#FAFAFA",
                border: "1px solid #d9d9d9",
                borderRadius: "10px",
                boxShadow: "none",
                cursor: navigable ? "pointer" : "default",
                transition: "all 0.2s ease-in-out",
                "&:hover": navigable
                  ? {
                        backgroundColor: colors.background.light,
                        border: `1px solid ${primaryColor}`,
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                      }
                    : {},
              }}
            >
              <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                  }}
                >
                  <DescriptionIcon
                    sx={{
                      fontSize: 20,
                      flexShrink: 0,
                      color: primaryColor,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <DocumentNameWithPages fileName={grouped.document_name} />
                    {(grouped.property_name || grouped.entity_label) && (
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: "#989898",
                          fontFamily: "'Noto Sans JP', sans-serif",
                        }}
                      >
                        <CustomTruncateText
                          text={[grouped.property_name, grouped.entity_label]
                            .filter(Boolean)
                            .join(" / ")}
                        />
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
      {showLabels && (
        <Typography
          sx={{
            fontSize: 10,
            color: "#989898",
            mt: 1,
            fontFamily: "'Noto Sans JP', sans-serif",
          }}
        >
          ※文字が薄い場合や手書きの場合は抽出できないことがあります
        </Typography>
      )}
      {previewFile && (
        <CustomFileModal
          open={previewOpen}
          onClose={handleClosePreview}
          imageSrc={previewFile.url}
          imageName={previewFile.name}
        />
      )}
    </Box>
  );
};
